import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { openSurgeryPDF } from "../../lib/pdf";
import { useAuth } from "../../context/AuthContext";
import { Sparkles, Loader2, Save, Printer, Stethoscope, ClipboardList, HeartPulse, QrCode, Droplet, X } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import ShareQRDialog from "../ShareQRDialog";

const NOTES = {
  pre_op: {
    label: "Prequirúrgica", icon: Stethoscope, aiType: "surgery_pre",
    fields: [
      ["diagnostico_preoperatorio", "Diagnóstico preoperatorio"],
      ["procedimiento_planeado", "Procedimiento planeado"],
      ["ayuno", "Ayuno"],
      ["riesgo_quirurgico", "Riesgo quirúrgico"],
      ["estudios_previos", "Estudios previos"],
      ["plan_anestesico", "Plan anestésico"],
      ["notas", "Notas"],
    ],
  },
  post_op: {
    label: "Postquirúrgica", icon: ClipboardList, aiType: "surgery_post",
    fields: [
      ["diagnostico_postoperatorio", "Diagnóstico postoperatorio"],
      ["procedimiento_realizado", "Procedimiento realizado"],
      ["hallazgos", "Hallazgos"],
      ["sangrado_estimado", "Sangrado estimado"],
      ["complicaciones", "Complicaciones"],
      ["estado_paciente", "Estado del paciente"],
      ["notas", "Notas"],
    ],
  },
  post_indications: {
    label: "Indicaciones", icon: HeartPulse, aiType: "surgery_indications",
    fields: [
      ["medicamentos", "Medicamentos (uno por línea)"],
      ["liquidos_iv", "Líquidos intravenosos (ClinIV)"],
      ["cuidados_herida", "Cuidados de la herida"],
      ["dieta", "Dieta"],
      ["actividad", "Actividad"],
      ["signos_alarma", "Signos de alarma (uno por línea)"],
      ["cita_control", "Cita de control"],
      ["notas", "Notas"],
    ],
  },
};

const PDF_KIND = { pre_op: "pre", post_op: "post", post_indications: "indications" };
const SHARE_KIND = { pre_op: "nota_prequirurgica", post_op: "nota_postquirurgica", post_indications: "indicaciones_postquirurgicas" };
const SHARE_TITLE = { pre_op: "Nota prequirúrgica", post_op: "Nota postquirúrgica", post_indications: "Indicaciones postquirúrgicas" };

// Convierte el JSON de la IA a texto plano por campo (arrays → líneas).
const fromAI = (noteKey, parsed) => {
  const out = {};
  const fields = NOTES[noteKey].fields;
  for (const [k] of fields) {
    let v = parsed?.[k];
    if (Array.isArray(v)) {
      if (k === "medicamentos") {
        v = v.map((m) => [m.nombre, m.presentacion, [m.dosis, m.frecuencia, m.duracion].filter(Boolean).join(" ")].filter(Boolean).join(" — ")).join("\n");
      } else {
        v = v.join("\n");
      }
    }
    out[k] = v || "";
  }
  return out;
};

export default function SurgeryNotes({ appointment, patient }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("pre_op");
  const [data, setData] = useState(() => ({
    pre_op: { ...(appointment.surgery?.pre_op || {}) },
    post_op: { ...(appointment.surgery?.post_op || {}) },
    post_indications: { ...(appointment.surgery?.post_indications || {}) },
  }));
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareDoc, setShareDoc] = useState(null);
  const [clinIVOpen, setClinIVOpen] = useState(false);

  // ClinIV (abierto desde postqx) envía su resultado y se coloca en "Líquidos IV".
  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.type === "cliniv-result" && typeof e.data.text === "string") {
        setData((d) => ({ ...d, post_indications: { ...d.post_indications, liquidos_iv: e.data.text } }));
        setClinIVOpen(false);
        setTab("post_indications");
        toast.success("Resultado de ClinIV agregado a las indicaciones postqx");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const note = data[tab];
  const cfg = NOTES[tab];

  const setField = (k, v) => setData((d) => ({ ...d, [tab]: { ...d[tab], [k]: v } }));

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/ai/assist", {
        patient_id: patient.patient_id, type: cfg.aiType,
        procedure: appointment.reason || data.pre_op?.procedimiento_planeado || "",
        diagnosis: data.pre_op?.diagnostico_preoperatorio || "",
      });
      const p = res.data.parsed;
      if (!p) { toast.error("La IA no devolvió datos"); return; }
      setData((d) => ({ ...d, [tab]: { ...d[tab], ...fromAI(tab, p) } }));
      toast.success("Borrador generado con IA. Revísalo y edítalo.");
    } catch { toast.error("Error de IA"); }
    finally { setAiLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/appointments/${appointment.appointment_id}/surgery`, { [tab]: note });
      toast.success(`Nota ${cfg.label.toLowerCase()} guardada`);
      window.dispatchEvent(new CustomEvent("clinped:appointments-updated"));
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const pdf = () => {
    const dateLabel = (() => { try { return new Date(appointment.scheduled_at).toLocaleString("es-MX"); } catch { return ""; } })();
    openSurgeryPDF({ patient, doctor: user, kind: PDF_KIND[tab], note, dateLabel });
  };

  const share = async () => {
    const items = cfg.fields.map(([k, label]) => (note[k] && String(note[k]).trim()) ? `${label}: ${note[k]}` : null).filter(Boolean);
    if (items.length === 0) { toast.error("Completa la nota antes de compartir"); return; }
    setSharing(true);
    try {
      await api.put(`/appointments/${appointment.appointment_id}/surgery`, { [tab]: note });
      const res = await api.post("/prescriptions", {
        patient_id: patient.patient_id, kind: SHARE_KIND[tab], title: SHARE_TITLE[tab],
        diagnosis: appointment.reason || "", sections: [{ heading: SHARE_TITLE[tab], items }],
      });
      setShareDoc(res.data);
    } catch { toast.error("No se pudo generar el QR"); }
    finally { setSharing(false); }
  };

  return (
    <div className="mt-3 bg-brand-sage/5 border border-brand-sage/20 rounded-2xl p-3.5" data-testid={`surgery-notes-${appointment.appointment_id}`}>
      <div className="flex bg-white rounded-full p-1 w-fit mb-3 shadow-sm">
        {Object.entries(NOTES).map(([k, c]) => (
          <button key={k} data-testid={`surgery-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${tab === k ? "bg-brand-ink text-white" : "text-brand-ink/60"}`}>
            <c.icon size={13} /> {c.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button data-testid="surgery-ai" onClick={generateAI} disabled={aiLoading}
          className="flex items-center gap-1.5 bg-brand-terracotta text-white text-xs font-bold px-3 py-2 rounded-xl disabled:opacity-60">
          {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generar borrador con IA
        </button>
        <button data-testid="surgery-save" onClick={save} disabled={saving}
          className="flex items-center gap-1.5 bg-brand-ink text-white text-xs font-bold px-3 py-2 rounded-xl disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
        </button>
        {tab === "post_indications" && (
          <button data-testid="surgery-cliniv" onClick={() => setClinIVOpen(true)}
            className="flex items-center gap-1.5 bg-[#0b5f73] text-white text-xs font-bold px-3 py-2 rounded-xl hover:brightness-110">
            <Droplet size={14} /> Calcular líquidos IV (ClinIV)
          </button>
        )}
        <button data-testid="surgery-pdf" onClick={pdf}
          className="flex items-center gap-1.5 border border-stone-200 text-brand-ink text-xs font-bold px-3 py-2 rounded-xl hover:bg-white">
          <Printer size={14} /> PDF
        </button>
        <button data-testid="surgery-share" onClick={share} disabled={sharing}
          className="flex items-center gap-1.5 bg-brand-sage text-white text-xs font-bold px-3 py-2 rounded-xl disabled:opacity-60">
          {sharing ? <Loader2 size={14} className="animate-spin" /> : <QrCode size={14} />} Compartir QR
        </button>
      </div>

      <div className="space-y-2.5">
        {cfg.fields.map(([k, label]) => {
          const multiline = ["notas", "medicamentos", "signos_alarma", "hallazgos", "cuidados_herida", "estudios_previos", "liquidos_iv"].includes(k);
          return (
            <div key={k}>
              <label className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{label}</label>
              {multiline ? (
                <Textarea data-testid={`surgery-field-${k}`} value={note[k] || ""} onChange={(e) => setField(k, e.target.value)}
                  rows={2} className="rounded-xl bg-white border-transparent mt-1 text-sm" />
              ) : (
                <Input data-testid={`surgery-field-${k}`} value={note[k] || ""} onChange={(e) => setField(k, e.target.value)}
                  className="rounded-xl bg-white border-transparent mt-1 text-sm" />
              )}
            </div>
          );
        })}
      </div>
      <ShareQRDialog doc={shareDoc} onClose={() => setShareDoc(null)} />
      {clinIVOpen && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-3 sm:p-6" data-testid="cliniv-modal">
          <div className="bg-white rounded-2xl w-full max-w-5xl h-[86vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-stone-100">
              <p className="font-outfit font-bold text-brand-ink flex items-center gap-2"><Droplet size={16} className="text-[#0b5f73]" /> ClinIV · Soluciones IV pediátricas</p>
              <button data-testid="cliniv-close" onClick={() => setClinIVOpen(false)} className="p-1.5 rounded-lg hover:bg-stone-100"><X size={18} /></button>
            </div>
            <iframe title="ClinIV" src="/cliniv.html?embed=1" className="flex-1 w-full border-0" />
          </div>
        </div>
      )}
    </div>
  );
}
